import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { Passenger, PassengerImage } from "../shared/models/passenger.model";

enum EndpointRoutes {
    SAVE_PASSENGERS = "passengers/save",
    // INSERT_PASSENGERS = 'passenger/insert',
    GET_ALL_PASSENGER = "passengers/get",
    UPDATE_PASSENGER = "passenger/update",
    INSERT_PASSENGER = "passenger/insert",
    UPLOAD_IDCARD = "passenger/saveImage",
    DELETE_PASSENGER = "passenger/delete",
    SEARCH_PASSENGERS = "passengers/search",
    GET_IMAGES = "passenger/getImages",
    UPLOAD_IDCARDIMAGE = "passenger/saveImage",
    DELETE_IDCARDIMAGE = "passenger/deleteImage",
}

@Injectable({
    providedIn: "root",
})
export class PassengersService {
    private _passengers: Passenger[] = [];

    public onPassengerEdit: Subject<void> = new Subject();
    public passengersListChange: BehaviorSubject<{ flightId: number; passengers: Passenger[] }> = new BehaviorSubject<{ flightId: number; passengers: Passenger[] }>(null);

    constructor(private _http: HttpClient) {}

    public setPassengers(passengers: Passenger[]): void {
        this._passengers = passengers;
    }

    public getPassengers(): Passenger[] {
        return this._passengers.slice();
    }

    private _updatePassengerInList(formObject: Passenger): void {
        this._passengers.map((element, index) => {
            if (element.id === formObject.id) {
                this._passengers[index] = formObject;
            }
        });
    }

    private _deletePassengerFromList(passengerId: number): void {
        const filterPassenger = this._passengers.filter((p) => p.id === passengerId)[0];
        const index = this._passengers.indexOf(filterPassenger);
        this._passengers.splice(index, 1);
    }

    private _updatePassengerImageCount(passengerId: number, increment: boolean) {
        const filterPassenger = this._passengers.filter((p) => p.id === passengerId)[0];
        filterPassenger.imageCount = increment ? (!filterPassenger.imageCount ? 1 : filterPassenger.imageCount + 1) : filterPassenger.imageCount - 1;
    }

    public updatePassengersList(passengersArray: Passenger[]) {
        // return this._http.put<{ message: Passenger[] }>(environment.serverUrl + EndpointRoutes.SAVE_PASSENGERS,
        return this._http.put<Passenger[]>(environment.serverUrl + EndpointRoutes.SAVE_PASSENGERS, { passengers: passengersArray }).pipe(
            map((responseData) => {
                this.passengersListChange.next({ flightId: passengersArray[0].flightId, passengers: responseData });
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getAllPassengers(): Observable<Passenger[]> {
        return this._http.get<Passenger[]>(environment.serverUrl + EndpointRoutes.GET_ALL_PASSENGER).pipe(
            map((responseData) => {
                this._passengers = responseData;
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public updatePassenger(passenger: Passenger): Observable<Passenger> {
        return this._http.post<Passenger>(environment.serverUrl + EndpointRoutes.UPDATE_PASSENGER, { passenger }).pipe(
            map((responseData) => {
                this._updatePassengerInList(responseData);
                this.onPassengerEdit.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }
    public insertPassenger(passenger: Passenger): Observable<Passenger> {
        return this._http.put<Passenger>(environment.serverUrl + EndpointRoutes.INSERT_PASSENGER, { passenger }).pipe(
            map((responseData) => {
                this._passengers.push(responseData);
                this.onPassengerEdit.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public uploadIdCardImage(image: File, passengerId: string) {
        const formData = new FormData();
        formData.append("id", passengerId);
        formData.append("image", image);
        return this._http.post<any>(environment.serverUrl + EndpointRoutes.UPLOAD_IDCARD, formData).pipe(
            map((responseData) => {
                // console.log(responseData);
                // this.getAllPassengers().subscribe(() => {
                //   this.onPassengerEdit.next();
                // });
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public softDeletePassengerById(passengerId: number) {
        let params = new HttpParams();
        params = params.append("id", passengerId.toString());
        return this._http.delete<any>(environment.serverUrl + EndpointRoutes.DELETE_PASSENGER, { params: params }).pipe(
            map((responseData) => {
                this._deletePassengerFromList(passengerId);
                this.onPassengerEdit.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public searchPassengersByFlightCode(flightCode: string) {
        let params = new HttpParams();
        params = params.append("type", "1");
        params = params.append("flightCode", flightCode);
        return this._http.get<Passenger[]>(environment.serverUrl + EndpointRoutes.SEARCH_PASSENGERS, { params }).pipe(
            map((responseData) => {
                this._passengers = responseData;
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public searchPassengersByName(filter: { name: string } = { name: "" }): Observable<Passenger[]> {
        let params = new HttpParams();
        params = params.append("type", "2");
        params = params.append("name", filter.name);
        return this._http.get<Passenger[]>(environment.serverUrl + EndpointRoutes.SEARCH_PASSENGERS, { params }).pipe(
            tap((response: Passenger[]) => {
                return response;
            }),
        );
    }

    public getImagesByPassengerId(passengerId: number): Observable<PassengerImage[]> {
        let params = new HttpParams();
        params = params.append("passengerId", passengerId.toString());
        return this._http.get<PassengerImage[]>(environment.serverUrl + EndpointRoutes.GET_IMAGES, { params }).pipe(
            tap((response: PassengerImage[]) => {
                return response;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public uploadPassengerIdCardImage(passengerId: number, image: File) {
        const formData = new FormData();
        formData.append("passengerId", passengerId.toString());
        formData.append("image", image);
        return this._http.post<any>(environment.serverUrl + EndpointRoutes.UPLOAD_IDCARDIMAGE, formData).pipe(
            map((responseData) => {
                this._updatePassengerImageCount(passengerId, true);
                this.onPassengerEdit.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public deletePassengerIdCardImage(id: number, passengerId: number) {
        let params = new HttpParams();
        params = params.append("id", id.toString());
        params = params.append("passengerId", passengerId.toString());
        return this._http.delete<any>(environment.serverUrl + EndpointRoutes.DELETE_IDCARDIMAGE, { params: params }).pipe(
            map((responseData) => {
                this._updatePassengerImageCount(passengerId, false);
                this.onPassengerEdit.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }
}
